import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { Subscription } from 'rxjs';

import { Alert } from '../../../models/alert.model';
import { AlertEnum } from '../messages.enum';
import { MessageService } from '../message.service';

@Component({
    selector: 'alert',
    templateUrl: './alert.component.html',
    styleUrls: ['./alert.component.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
})
export class AlertComponent implements OnInit, OnDestroy {
  alerts: Alert[] = [];

  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.messageService.startAlert().subscribe((incoming: Alert | null | undefined) => {
        // Historical behavior (PlantillaWeb): a falsy alert clears the stack.
        if (!incoming) {
          this.alerts = [];
          return;
        }

        const alert = new Alert(incoming);
        this.applyAlertVisuals(alert);
        this.removeDuplicates(alert);

        this.alerts = [...this.alerts, alert];

        // Auto-dismiss (8s) cuando Remove=true.
        if (alert.remove) {
          const lifetimeMs = alert.timer && alert.timer > 0 ? alert.timer : 8000;
          window.setTimeout(() => this.dismiss(alert), lifetimeMs);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  dismiss(alert: Alert, withAnimation = true): void {
    if (!this.alerts.some((a) => a.alertId === alert.alertId)) return;

    if (withAnimation) {
      alert.animation = 'slideOutDown fast';
      // Wait for CSS animation to finish, then remove from stack.
      window.setTimeout(() => {
        this.alerts = this.alerts.filter((a) => a.alertId !== alert.alertId);
      }, 220);
      return;
    }

    this.alerts = this.alerts.filter((a) => a.alertId !== alert.alertId);
  }

  private applyAlertVisuals(alert: Alert): void {
    switch (alert.type) {
      case AlertEnum.Success:
        alert.icon = 'pi pi-check';
        alert.class = 'success';
        break;
      case AlertEnum.Warning:
        alert.icon = 'pi pi-exclamation-triangle';
        alert.class = 'warning';
        break;
      case AlertEnum.Error:
        alert.icon = 'pi pi-times-circle';
        alert.class = 'error';
        break;
      case AlertEnum.Information:
        alert.icon = 'pi pi-info-circle';
        alert.class = 'info';
        break;
      default:
        alert.icon = 'pi pi-info-circle';
        alert.class = 'info';
        break;
    }

    // Default entry animation
    if (!alert.animation) {
      alert.animation = 'slideInUp';
    }
  }

  private removeDuplicates(incoming: Alert): void {
    for (const existing of this.alerts) {
      if (existing.description === incoming.description && existing.type === incoming.type) {
        this.dismiss(existing, false);
      }
    }
  }

  /** Separa título y mensaje cuando la descripción usa " — ". */
  getAlertParts(description: string): { title: string | null; message: string } {
    const sep = ' — ';
    const i = description.indexOf(sep);
    if (i === -1) return { title: null, message: description };
    return { title: description.slice(0, i).trim(), message: description.slice(i + sep.length).trim() };
  }

  constructor(private readonly messageService: MessageService) {}
}
